import type { Theme, SxProps, CSSObject } from '@mui/material/styles';

import Box from '@mui/material/Box';
import TableRow from '@mui/material/TableRow';
import Checkbox from '@mui/material/Checkbox';
import TableHead from '@mui/material/TableHead';
import TableCell from '@mui/material/TableCell';
import Tooltip from '@mui/material/Tooltip';
import TableSortLabel from '@mui/material/TableSortLabel';

import { useLocales } from 'src/locales';
import { Iconify } from 'src/components/iconify';

// ----------------------------------------------------------------------

/**
 * Off-screen but still in the accessibility tree — `display: none` would take it
 * out of both. Exported because a table cell sometimes has to say something a
 * sighted reader gets from layout alone; one copy, so the technique cannot drift.
 */
export const visuallyHidden: CSSObject = {
  border: 0,
  padding: 0,
  width: '1px',
  height: '1px',
  margin: '-1px',
  overflow: 'hidden',
  position: 'absolute',
  whiteSpace: 'nowrap',
  clip: 'rect(0 0 0 0)',
};

// ----------------------------------------------------------------------

export type TableHeadCellProps = {
  field: string;
  title?: string;
  width?: CSSObject['width'];
  align?: 'left' | 'center' | 'right';
  sx?: SxProps<Theme>;
  /** Render a plain header (no sort affordance) even when onSort is provided. */
  disableSort?: boolean;
  /** sortBy value sent to the API when it differs from the display field (e.g. wine_type_icon sorts as wine_type). */
  sortKey?: string;
  /** Disclosure shown on an info affordance beside the title — e.g. that a column ignores the period filter. */
  tooltip?: string;
  /**
   * NAT-2711 — `name`: the row's identifying text (a wine, a customer, a counterparty).
   * At least 240 px wide; its body cells should render `<TableNameCell>` for the 2-line clamp.
   */
  kind?: 'name';
  /**
   * NAT-2711 — a second order this column offers, as a small button under the title. The stats
   * tables use it for the period change: the header mirrors the cell, value above, change below.
   * `field` is what `onSort` receives and what `orderBy` marks; `title` is the visible label;
   * `ariaLabel` names the button in full.
   */
  secondarySort?: { field: string; title: string; ariaLabel: string };
};

export type TableHeadCustomProps = {
  orderBy?: string;
  rowCount?: number;
  sx?: SxProps<Theme>;
  numSelected?: number;
  order?: 'asc' | 'desc';
  headLabel: TableHeadCellProps[];
  onSort?: (id: string) => void;
  onSelectAllRows?: (checked: boolean) => void;
  /**
   * NAT-2711 — pin the first column while the table scrolls sideways. Default: on when the
   * table has more than STICKY_FIRST_COLUMN_MIN columns. Pass `false` when the first column is a
   * marker (an icon, a priority dot, an expander) rather than the row's name.
   */
  stickyFirstColumn?: boolean;
};

/** A table wider than this many columns pins its first one. */
export const STICKY_FIRST_COLUMN_MIN = 8;
/** The least width of a `kind: 'name'` column. */
export const NAME_COLUMN_MIN_WIDTH = 240;
export const tableHeadCustomClasses = { stickyFirstColumn: 'table-head-custom--sticky-first' };

// A theme without CSS variables (a bare ThemeProvider in a test) has no `vars`.
const palette = (theme: Theme) => (theme.vars ?? theme).palette;

const stickyCell = (theme: Theme) => ({
  position: 'sticky',
  left: 0,
  // The divider moves with the cell; a border would stay behind in a border-collapse table.
  boxShadow: `inset -1px 0 0 ${palette(theme).divider}`,
});

export function TableHeadCustom({
  sx,
  order,
  onSort,
  orderBy,
  headLabel,
  rowCount = 0,
  numSelected = 0,
  onSelectAllRows,
  stickyFirstColumn,
}: TableHeadCustomProps) {
  const { t } = useLocales();

  // With selectable rows the checkbox would be the pinned column, so it never pins then.
  const sticky =
    !onSelectAllRows && (stickyFirstColumn ?? headLabel.length > STICKY_FIRST_COLUMN_MIN);

  return (
    <TableHead
      className={sticky ? tableHeadCustomClasses.stickyFirstColumn : undefined}
      sx={[
        (theme) => ({
          position: 'sticky',
          top: 0,
          // Sticky: the thead is its own stacking context, so it must sit above the body's pinned
          // cells (z 1) as a whole; the corner th is ordered inside it.
          zIndex: sticky ? 3 : 1,
          backgroundColor: theme.palette.background.paper,
          ...(sticky && {
            // Header corner: above the header's own cells (MUI's stickyHeader gives every th z 2),
            // inside the thead; the header as a whole is above the body.
            '& > tr > th:first-of-type': { ...stickyCell(theme), zIndex: 3 },
            // Body and footer: opaque, so scrolled figures pass under it; the row's own tint
            // (hover, selected, status) is repainted on top through --row-bg.
            '& ~ tbody > tr > td:first-of-type:not([colspan]), & ~ tfoot > tr > td:first-of-type:not([colspan])':
              {
                ...stickyCell(theme),
                zIndex: 1,
                backgroundColor: palette(theme).background.paper,
                backgroundImage:
                  'linear-gradient(var(--row-bg, transparent), var(--row-bg, transparent))',
              },
          }),
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <TableRow>
        {onSelectAllRows && (
          <TableCell padding="checkbox">
            <Checkbox
              indeterminate={!!numSelected && numSelected < rowCount}
              checked={!!rowCount && numSelected === rowCount}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                onSelectAllRows(event.target.checked)
              }
              slotProps={{
                input: {
                  id: `all-row-checkbox`,
                  'aria-label': t('select_all_rows'),
                },
              }}
            />
          </TableCell>
        )}

        {headLabel.map((headCell) => {
          const sortField = headCell.sortKey ?? headCell.field;
          const secondary = headCell.secondarySort;
          const primarySorted = orderBy === sortField;
          const secondarySorted = !!secondary && orderBy === secondary.field;
          const sorted = primarySorted || secondarySorted;
          const sortedWording = order === 'desc' ? t('sorted_descending') : t('sorted_ascending');
          const sortable = !!onSort && !headCell.disableSort;

          const primaryLabel = sortable ? (
            <TableSortLabel
              hideSortIcon
              data-sort-field={sortField}
              active={primarySorted}
              direction={primarySorted ? order : 'asc'}
              onClick={() => onSort(sortField)}
            >
              {headCell.title}

              {primarySorted ? (
                <Box component="span" sx={visuallyHidden}>
                  {sortedWording}
                </Box>
              ) : null}
            </TableSortLabel>
          ) : (
            headCell.title
          );

          const label =
            sortable && secondary ? (
              <Box
                sx={{
                  display: 'inline-flex',
                  flexDirection: 'column',
                  alignItems: headCell.align === 'right' ? 'flex-end' : 'flex-start',
                }}
              >
                {primaryLabel}
                <TableSortLabel
                  hideSortIcon
                  data-sort-field={secondary.field}
                  data-sort-secondary
                  // The visible text leads the name (WCAG 2.5.3); the sorted wording rides in it,
                  // because an aria-label replaces the content.
                  aria-label={`${secondary.title} — ${secondary.ariaLabel}${
                    secondarySorted ? `, ${sortedWording}` : ''
                  }`}
                  active={secondarySorted}
                  direction={secondarySorted ? order : 'asc'}
                  onClick={() => onSort(secondary.field)}
                  sx={{ typography: 'caption', color: 'text.secondary' }}
                >
                  {secondary.title}
                </TableSortLabel>
              </Box>
            ) : (
              primaryLabel
            );

          // The affordance sits beside the label rather than wrapping it: a sortable header
          // is a button, and a focusable span inside it would put a second tab stop in the
          // middle of the sort control. Cells without a tooltip render exactly as before.
          const content = headCell.tooltip ? (
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
              {label}
              <Tooltip title={headCell.tooltip} placement="top" arrow>
                <Box
                  component="span"
                  tabIndex={0}
                  role="img"
                  aria-label={headCell.tooltip}
                  sx={{ display: 'inline-flex', cursor: 'help', color: 'text.disabled' }}
                >
                  <Iconify icon="eva:info-outline" width={14} />
                </Box>
              </Tooltip>
            </Box>
          ) : (
            label
          );

          return (
            <TableCell
              key={headCell.field}
              align={headCell.align || 'left'}
              sortDirection={sorted ? order : false}
              sx={{
                width: headCell.width,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                fontWeight: 'fontWeightBold',
              }}
            >
              {headCell.kind === 'name' ? (
                // An inner box forces the width reliably; min-width on a <th> is not honoured
                // by every engine.
                <Box
                  component="span"
                  sx={{ display: 'inline-block', minWidth: NAME_COLUMN_MIN_WIDTH }}
                >
                  {content}
                </Box>
              ) : (
                content
              )}
            </TableCell>
          );
        })}
      </TableRow>
    </TableHead>
  );
}
